'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useEffect, useState } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Box, Typography, useTheme } from '@mui/material';
import { useLineChartStyles } from './useLineChartStyles';
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);
const LineChart = ({ title, labels, datasetLabel, data, lineColor, fillColor, pointColor, useGradient = true, useFillColor = true }) => {
    const theme = useTheme();
    const styles = useLineChartStyles();
    const chartRef = useRef(null);
    const [gradient, setGradient] = useState(null);
    useEffect(() => {
        const chart = chartRef.current;
        if (chart && chart.ctx && !gradient) {
            const ctx = chart.ctx;
            const gradientFill = ctx.createLinearGradient(0, 0, 0, chart.height);
            gradientFill.addColorStop(0, 'rgba(154, 208, 239, 0.02)');
            setGradient(gradientFill);
        }
    }, [chartRef, theme, gradient]);
    const chartData = {
        labels,
        datasets: [
            {
                label: datasetLabel,
                data,
                borderColor: lineColor || theme.palette.primary.main,
                backgroundColor: useGradient && gradient
                    ? gradient
                    : useFillColor && fillColor
                        ? fillColor
                        : theme.palette.primary.light,
                pointBackgroundColor: pointColor || lineColor || theme.palette.primary.main,
                fill: true,
                tension: 0.4,
                borderWidth: 2,
                pointRadius: 2,
                hoverRadius: 1
            }
        ]
    };
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
                labels: {
                    usePointStyle: true,
                    pointStyle: 'circle',
                    padding: 20,
                    font: { size: 12 },
                    generateLabels: (chart) => {
                        const labels = ChartJS.defaults.plugins.legend.labels.generateLabels(chart);
                        labels.forEach((label) => {
                            label.pointStyle = 'circle';
                            label.fillStyle = label.strokeStyle;
                        });
                        return labels;
                    }
                },
                onHover: (e, legendItem, legend) => {
                    if (e.native?.target)
                        e.native.target.style.cursor = 'pointer';
                    let tooltip = document.getElementById('chart-legend-tooltip');
                    if (!tooltip) {
                        tooltip = document.createElement('div');
                        tooltip.id = 'chart-legend-tooltip';
                        tooltip.style.position = 'absolute';
                        tooltip.style.backgroundColor = 'rgba(0,0,0,0.7)';
                        tooltip.style.color = 'white';
                        tooltip.style.padding = '5px 10px';
                        tooltip.style.borderRadius = '4px';
                        tooltip.style.fontSize = '12px';
                        tooltip.style.pointerEvents = 'none';
                        tooltip.style.zIndex = '1000';
                        document.body.appendChild(tooltip);
                    }
                    tooltip.textContent = 'Assets legend tooltip';
                    if (e.native) {
                        tooltip.style.left = e.native.pageX + 'px';
                        tooltip.style.top = e.native.pageY + 10 + 'px';
                    }
                    tooltip.style.display = 'block';
                },
                onLeave: (e) => {
                    if (e.native?.target)
                        e.native.target.style.cursor = 'default';
                    const tooltip = document.getElementById('chart-legend-tooltip');
                    if (tooltip)
                        tooltip.style.display = 'none';
                }
            },
            tooltip: {
                mode: 'point',
                usePointStyle: true,
                intersect: true,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                titleColor: '#333',
                bodyColor: '#666',
                borderColor: '#ddd',
                borderWidth: 1,
                padding: 10,
                boxPadding: 5,
                cornerRadius: 4,
                titleFont: { size: 14, weight: 'bold' },
                bodyFont: { size: 13 },
                callbacks: {
                    label: (context) => `${context.dataset.label}: ${context.parsed.y}`
                }
            }
            // Disable datalabels plugin for line chart
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: { display: false },
                border: { display: false }
            },
            y: {
                beginAtZero: true,
                grid: { display: false },
                ticks: { display: false },
                border: { display: false }
            }
        },
        elements: {
            line: {
                tension: 0.4,
                borderWidth: 2
            },
            point: {
                radius: 0,
                hoverRadius: 0
            }
        },
        interaction: {
            mode: 'point',
            intersect: true
        }
    };
    return (_jsxs(Box, { sx: styles.container, children: [title && (_jsx(Box, { mb: 2.6, display: 'flex', justifyContent: 'center', alignItems: 'center', children: _jsx(Typography, { variant: 'h6', sx: styles.title, children: title }) })), _jsx(Box, { sx: styles.chartContainer, children: _jsx(Line, { ref: chartRef, data: chartData, options: options }) })] }));
};
export default LineChart;
